import { flash } from "@xola/ui-kit";
import { AxiosError } from "axios";
import { useFormikContext } from "formik";
import { omit } from "lodash";
import { useCallback, useRef, useState } from "react";
import { useMutation } from "react-query";
import { Merchandise, MerchandiseCatalogItemStatus, OversellPolicy, Product } from "types";
import { MerchandiseItemsFormValues } from "../merchandiseHelper";
import {
    getCatalogItemsWithChangedPatchItems,
    getCatalogItemsWithDraftValues,
    getCatalogItemsWithSelectedStatus,
    getCatalogItemsWithUpdatedItem,
    getCatalogItemsWithUpdatedLowInventory,
    getCatalogItemsWithUpdatedOversellPolicy,
    getCatalogItemsWithUpdatedPrice,
    getChangedCatalogItemsPayloadItems,
    getCleanedUpDraftItemsAfterBulkPriceSave,
    getLowInventoryPatchCatalogItemsPayload,
    getOversellPolicyPatchCatalogItemsPayload,
    getPatchCatalogItemsPayload,
    getPayloadCandidateItemsFromCatalogItems,
    getPricePatchCatalogItemsPayload,
    getStatusPatchCatalogItemsPayload,
    getUpdatedOriginalCatalogIdentityMapWithChangedItems,
    getUpdatedOriginalCatalogIdentityMapWithPrice,
    PatchCatalogItemsPayload,
    VariantCatalogIdentity,
    VariantDraftItems,
} from "./variantGroupItemInventoryPayloadHelper";
import { VariantCatalogItem } from "./VariantTableHelper";
import { api } from "@/api/api";
import { useInvalidate } from "@/hooks/useInvalidation";
import { MERCHANDISE_API_PATH, MERCHANDISE_CACHE_KEY } from "@/models/Merchandise";
import { updateCache } from "@/utils/updateCache";

const getCatalogItemPrice = (item: VariantCatalogItem): number | null => {
    return item.prices?.perUnit?.price ?? item.price ?? null;
};

const extractDuplicateSkuItemIds = (error: unknown): Set<string> => {
    const data = (error as AxiosError)?.response?.data as any;
    const errors = data?.field?.ops?.errors;
    if (!errors || typeof errors !== "object") return new Set();
    return new Set(
        Object.values(errors)
            .filter((e: any) => e?.reason === "duplicate_sku" && e?.id)
            .map((e: any) => e.id as string),
    );
};

const refreshProductCaches = async (merchandiseId: string) => {
    const [{ data: product }, { data: ware }] = await Promise.all([
        api.get<Product>(`/products/${merchandiseId}`),
        api.get<Merchandise>(`/${MERCHANDISE_API_PATH}/${merchandiseId}`),
    ]);
    updateCache("products", product);
    updateCache(MERCHANDISE_CACHE_KEY, ware);
};

interface UseVariantBulkSaveParams {
    merchandise: Merchandise;
    catalogItems: VariantCatalogItem[];
    setCatalogItems: React.Dispatch<React.SetStateAction<VariantCatalogItem[]>>;
    selectedItems: Set<string>;
    getSelectedLeafIds: (items: { isLeaf?: boolean; id?: string | null }[]) => string[];
    originalCatalogIdentityByIdRef: React.MutableRefObject<Map<string, VariantCatalogIdentity>>;
}

export const useVariantBulkSave = ({
    merchandise,
    catalogItems,
    setCatalogItems,
    selectedItems,
    getSelectedLeafIds,
    originalCatalogIdentityByIdRef,
}: UseVariantBulkSaveParams) => {
    const { values, setFieldValue } = useFormikContext<MerchandiseItemsFormValues>();
    const invalidate = useInvalidate();
    const [serverDuplicateSkuItemIds, setServerDuplicateSkuItemIds] = useState<Set<string>>(new Set());

    // Use a ref so saveChangedCatalogItems doesn't recreate on every keystroke
    const variantItemsRef = useRef(values.variantItems);
    variantItemsRef.current = values.variantItems;

    const { mutateAsync: patchCatalogItems } = useMutation(async (payload: PatchCatalogItemsPayload) => {
        return api.patch("/catalogItems", payload);
    });

    const updateSelectedStatus = useCallback(
        async (status: MerchandiseCatalogItemStatus) => {
            const selectedLeafIds = getSelectedLeafIds(catalogItems);
            if (selectedLeafIds.length === 0) {
                return;
            }

            const payload = getStatusPatchCatalogItemsPayload(selectedLeafIds, status);
            await patchCatalogItems(payload);
            setCatalogItems((previous) => getCatalogItemsWithSelectedStatus(previous, selectedItems, status));
            invalidate("catalogItems");
            await refreshProductCaches(merchandise.id);
        },
        [
            catalogItems,
            getSelectedLeafIds,
            selectedItems,
            patchCatalogItems,
            setCatalogItems,
            invalidate,
            merchandise.id,
        ],
    );

    const updateSelectedPrice = useCallback(
        async (price: number) => {
            const selectedLeafIds = getSelectedLeafIds(catalogItems);
            if (selectedLeafIds.length === 0) {
                return;
            }

            const nextPrice = Number(price);
            const variantItems: VariantDraftItems = variantItemsRef.current ?? {};

            try {
                await patchCatalogItems(getPricePatchCatalogItemsPayload(selectedLeafIds, nextPrice));

                setCatalogItems((previous) => getCatalogItemsWithUpdatedPrice(previous, selectedLeafIds, nextPrice));

                const nextVariantItems = getCleanedUpDraftItemsAfterBulkPriceSave(selectedLeafIds, variantItems);
                void setFieldValue("variantItems", nextVariantItems, false);

                originalCatalogIdentityByIdRef.current = getUpdatedOriginalCatalogIdentityMapWithPrice(
                    originalCatalogIdentityByIdRef.current,
                    selectedLeafIds,
                    nextPrice,
                );

                invalidate("catalogItems");
                await refreshProductCaches(merchandise.id);
                flash.success("Prices updated");
            } catch {
                flash.error("Could not update prices");
                throw new Error("Could not update prices");
            }
        },
        [
            catalogItems,
            getSelectedLeafIds,
            patchCatalogItems,
            setCatalogItems,
            setFieldValue,
            originalCatalogIdentityByIdRef,
            invalidate,
            merchandise.id,
        ],
    );

    const updateSelectedOversellPolicy = useCallback(
        async (oversellPolicy: OversellPolicy) => {
            const selectedLeafIds = getSelectedLeafIds(catalogItems);
            if (selectedLeafIds.length === 0) {
                return;
            }

            try {
                await patchCatalogItems(getOversellPolicyPatchCatalogItemsPayload(selectedLeafIds, oversellPolicy));

                setCatalogItems((previous) =>
                    getCatalogItemsWithUpdatedOversellPolicy(previous, selectedLeafIds, oversellPolicy),
                );
            } catch {
                flash.error("Could not update variants");
                throw new Error("Could not update variants");
            }

            // The policy is already persisted, so a failed cache refresh must not be
            // reported as a failed update — the next fetch reconciles the caches.
            invalidate("catalogItems");
            await refreshProductCaches(merchandise.id).catch(() => undefined);
            flash.success("Variants updated");
        },
        [catalogItems, getSelectedLeafIds, patchCatalogItems, setCatalogItems, invalidate, merchandise.id],
    );

    const updateSelectedLowInventory = useCallback(
        async (lowInventoryThreshold: number) => {
            const selectedLeafIds = getSelectedLeafIds(catalogItems);
            if (selectedLeafIds.length === 0) {
                return;
            }

            const nextThreshold = Number(lowInventoryThreshold);

            try {
                await patchCatalogItems(getLowInventoryPatchCatalogItemsPayload(selectedLeafIds, nextThreshold));

                setCatalogItems((previous) =>
                    getCatalogItemsWithUpdatedLowInventory(previous, selectedLeafIds, nextThreshold),
                );
            } catch {
                flash.error("Could not update variants");
                throw new Error("Could not update variants");
            }

            invalidate("catalogItems");
            await refreshProductCaches(merchandise.id).catch(() => undefined);
            flash.success("Variants updated");
        },
        [catalogItems, getSelectedLeafIds, patchCatalogItems, setCatalogItems, invalidate, merchandise.id],
    );

    const saveChangedCatalogItems = useCallback(async () => {
        const originalCatalogIdentityMap = originalCatalogIdentityByIdRef.current;
        const variantItems: VariantDraftItems = variantItemsRef.current ?? {};
        const catalogItemsWithDraftValues = getCatalogItemsWithDraftValues(catalogItems, variantItems);
        const payloadCandidateItems = getPayloadCandidateItemsFromCatalogItems(catalogItemsWithDraftValues);
        const changedItems = getChangedCatalogItemsPayloadItems(payloadCandidateItems, originalCatalogIdentityMap);

        if (changedItems.length === 0) {
            return;
        }

        try {
            const payload = getPatchCatalogItemsPayload(changedItems);
            await patchCatalogItems(payload);

            setServerDuplicateSkuItemIds(new Set());
            originalCatalogIdentityByIdRef.current = getUpdatedOriginalCatalogIdentityMapWithChangedItems(
                originalCatalogIdentityMap,
                changedItems,
            );

            setCatalogItems((previous) => getCatalogItemsWithChangedPatchItems(previous, changedItems));

            void setFieldValue("variantItems", {}, false);
            invalidate("catalogItems");
            await refreshProductCaches(merchandise.id);
            flash.success("Items saved successfully");
        } catch (error) {
            setServerDuplicateSkuItemIds(extractDuplicateSkuItemIds(error));
            throw error;
        }
    }, [
        catalogItems,
        invalidate,
        patchCatalogItems,
        setCatalogItems,
        setFieldValue,
        merchandise.id,
        originalCatalogIdentityByIdRef,
    ]);

    const handleSaveItem = useCallback(
        (updatedItem: VariantCatalogItem) => {
            setCatalogItems((previous) => getCatalogItemsWithUpdatedItem(previous, updatedItem));

            if (updatedItem.id) {
                const nextMap = new Map(originalCatalogIdentityByIdRef.current);
                nextMap.set(updatedItem.id, {
                    sku: updatedItem.sku ?? "",
                    barcode: updatedItem.barcode ?? "",
                    price: getCatalogItemPrice(updatedItem),
                });
                originalCatalogIdentityByIdRef.current = nextMap;

                const nextVariantItems = omit(variantItemsRef.current, updatedItem.id);
                void setFieldValue("variantItems", nextVariantItems, false);
            }
        },
        [setCatalogItems, originalCatalogIdentityByIdRef, setFieldValue],
    );

    return {
        updateSelectedStatus,
        updateSelectedPrice,
        updateSelectedOversellPolicy,
        updateSelectedLowInventory,
        saveChangedCatalogItems,
        handleSaveItem,
        serverDuplicateSkuItemIds,
    };
};
